import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hasAcademyManagementRole } from '@court-sync/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { AcademyGeneralSettingsTab } from '@/components/academy/AcademyGeneralSettingsTab';
import { AcademyLessonSettingsSection } from '@/components/organization/AcademyLessonSettingsSection';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAuthStore } from '@/store/auth';
import { useOrganizationSettingsData } from '@/hooks/useOrganizationSettingsData';
import { parseAcademyTab, type AcademySettingsTab } from '@/lib/settingsTabRoutes';
import { ACADEMY_SETTINGS_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  ACADEMY_SETTINGS_TOUR_KEY,
  scheduleAcademySettingsTourStart,
} from '@/lib/tours/academySettingsTour';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { TierUnlockBadge } from '@/components/tier/TierUnlockBadge';
import { useAcademySettingsTabUnlockTier } from '@/hooks/useTierUnlockBadge';

const ACADEMY_SETTINGS_TABS: {
  id: AcademySettingsTab;
  label: string;
  mobileLabel: string;
  tabId?: string;
}[] = [
  { id: 'general', label: 'General settings', mobileLabel: 'General' },
  {
    id: 'advanced',
    label: 'Advanced settings',
    mobileLabel: 'Advanced',
    tabId: ACADEMY_SETTINGS_TOUR_STEP_IDS.ADVANCED,
  },
];

const ACADEMY_SETTINGS_HELP = {
  title: 'Academy settings',
  description:
    'Set your academy name, configure age groups, and review advanced lesson policies on Professional or Enterprise.',
  tourLabel: 'Tour this page',
  tourDescription: 'Academy name, age groups, and advanced lesson settings.',
};

export default function AcademySettings() {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { academy, setAcademy, subtitle, isInitialLoading } = useOrganizationSettingsData();
  const handleTabChangeRef = useRef<(tab: AcademySettingsTab) => void>(() => {});
  const { startTourWithCompletionKey } = usePageTourActions();

  const hasAcademyManagement = Boolean(user && hasAcademyManagementRole(user));
  const activeTab = parseAcademyTab(searchParams.get('tab'));
  const advancedUnlockTier = useAcademySettingsTabUnlockTier('advanced');

  const handleTabChange = useCallback(
    (tab: AcademySettingsTab) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    handleTabChangeRef.current = handleTabChange;
  }, [handleTabChange]);

  const launchAcademySettingsTour = useCallback(() => {
    scheduleAcademySettingsTourStart(startTourWithCompletionKey, {
      onEnterGeneral: () => handleTabChangeRef.current('general'),
      onEnterAdvanced: () => handleTabChangeRef.current('advanced'),
    });
  }, [startTourWithCompletionKey]);

  useAutoPageTour(ACADEMY_SETTINGS_TOUR_KEY, Boolean(academy?.id) && !isInitialLoading, {
    onStart: launchAcademySettingsTour,
  });

  if (isInitialLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!hasAcademyManagement || !academy) {
    return (
      <div className="contents">
        <PageHeader title="Academy settings" subtitle="Academy not found" />
      </div>
    );
  }

  return (
    <div className="contents">
      <PageHeader
        title="Academy settings"
        subtitle={subtitle}
        help={
          <PageFeatureHelp
            title={ACADEMY_SETTINGS_HELP.title}
            tours={[
              {
                tourKey: ACADEMY_SETTINGS_TOUR_KEY,
                label: ACADEMY_SETTINGS_HELP.tourLabel,
                description: ACADEMY_SETTINGS_HELP.tourDescription,
                onStart: launchAcademySettingsTour,
              },
            ]}
          >
            <p>{ACADEMY_SETTINGS_HELP.description}</p>
          </PageFeatureHelp>
        }
        helpAnchorId={ACADEMY_SETTINGS_TOUR_STEP_IDS.GUIDE}
      />

      <div id={ACADEMY_SETTINGS_TOUR_STEP_IDS.TABS}>
        <DashboardTabNav<AcademySettingsTab>
          tabs={ACADEMY_SETTINGS_TABS.map((tab) =>
            tab.id === 'advanced'
              ? {
                  ...tab,
                  suffix: <TierUnlockBadge unlockTier={advancedUnlockTier} />,
                }
              : tab,
          )}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel="Academy settings sections"
        />
      </div>

      {activeTab === 'general' ? (
        <AcademyGeneralSettingsTab academy={academy} onSaved={setAcademy} />
      ) : null}

      {activeTab === 'advanced' ? (
        <AcademyLessonSettingsSection academy={academy} onSaved={setAcademy} />
      ) : null}
    </div>
  );
}
